import React, { useEffect, useMemo, useState } from "react";
import { IconClipboardList } from "@tabler/icons-react";
import {
  useEncoderClinicalCriteriaFile,
  useEncoderClinicalCriteriaFiles,
} from "../../hooks/queries";
import type { ClinicalCriteriaFileMeta } from "../../types/encoder";
import { criteriaFileToViewerSource } from "../../utils/encoder-clinical-criteria";
import { criteriaFileKey, cn, encoderErrorMessage } from "../../utils/encoder";
import ClinicalCriteriaAccordion from "./ClinicalCriteriaAccordion";
import { Spinner } from "./shared";

const ClinicalCriteriasTab: React.FC = () => {
  const filesQuery = useEncoderClinicalCriteriaFiles();
  const files: ClinicalCriteriaFileMeta[] = useMemo(
    () => filesQuery.data ?? [],
    [filesQuery.data]
  );
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return files;
    return files.filter((file) => file.name.toLowerCase().includes(term));
  }, [files, search]);

  useEffect(() => {
    if (!selectedKey && filtered[0]) {
      setSelectedKey(criteriaFileKey(filtered[0]));
    }
  }, [filtered, selectedKey]);

  const selected =
    filtered.find((file) => criteriaFileKey(file) === selectedKey) ??
    files.find((file) => criteriaFileKey(file) === selectedKey) ??
    null;
  const fileQuery = useEncoderClinicalCriteriaFile(selected);
  const body = criteriaFileToViewerSource(fileQuery.data);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-4 border-b border-gray-200 bg-white px-6 py-2">
        <h1 className="inline-flex items-center gap-2 text-lg font-bold text-gray-900">
          <IconClipboardList size={20} className="text-fuchsia-700" />
          Clinical Criterias
        </h1>
      </header>
      <div className="flex min-h-0 flex-1 overflow-hidden bg-[#f9fafb]">
        <aside className="flex w-[25%] min-w-[220px] max-w-[40%] shrink-0 flex-col border-r border-slate-200 bg-white">
          <div className="shrink-0 p-2">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search criteria..."
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
            {filesQuery.isPending ? (
              <Spinner label="Loading files..." />
            ) : filesQuery.isError ? (
              <p className="p-3 text-sm text-red-600">Failed to load files</p>
            ) : filtered.length === 0 ? (
              <p className="p-3 text-sm text-slate-500">No criteria found.</p>
            ) : (
              filtered.map((file: ClinicalCriteriaFileMeta) => {
                const key = criteriaFileKey(file);
                const active = key === selectedKey;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedKey(key)}
                    className={cn(
                      "mb-1 h-10 w-full truncate rounded-md px-3 text-left text-sm transition-colors",
                      active
                        ? "bg-blue-100 font-bold text-slate-900 shadow-sm"
                        : "hover:bg-slate-50"
                    )}
                    title={file.name}
                  >
                    {file.name}
                  </button>
                );
              })
            )}
          </div>
        </aside>
        <section className="min-w-0 flex-1 overflow-y-auto">
          <div className="p-2">
            {!selected ? (
              <p className="mt-20 text-center text-sm text-slate-500">
                Select a file to display its content here.
              </p>
            ) : fileQuery.isPending || (fileQuery.isFetching && !body) ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-500">
                <Spinner label={`Loading ${selected.name}…`} />
              </div>
            ) : fileQuery.isError ? (
              <p className="mt-20 text-center text-sm text-red-600">
                {encoderErrorMessage(
                  fileQuery.error,
                  "Failed to load criteria content"
                )}
              </p>
            ) : body ? (
              <ClinicalCriteriaAccordion content={body} fileName={selected.name} />
            ) : (
              <p className="mt-20 text-center text-sm text-slate-500">
                No content is available for this criteria.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ClinicalCriteriasTab;
